import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ExtensionMeta } from './lib/extension';

/**
 * Every folder under src/extensions/ with a meta.ts + index.tsx is an extension.
 * Nothing to register by hand: add the folder, it appears on the index page,
 * gets a route, and the sync script creates it in the stacks.
 */
const metas = import.meta.glob<{ meta: ExtensionMeta }>('./extensions/*/meta.ts', { eager: true });
const components = import.meta.glob<{ default: ComponentType }>('./extensions/*/index.tsx');

export interface RegistryEntry {
  meta: ExtensionMeta;
  load: () => Promise<{ default: ComponentType }>;
  /** Code-split component, created once here so pages never create components during render. */
  Component: LazyExoticComponent<ComponentType>;
}

export const registry: Record<string, RegistryEntry> = {};

for (const [path, mod] of Object.entries(metas)) {
  const dir = path.replace(/\/meta\.ts$/, '');
  const folder = dir.split('/').pop()!;
  const load = components[`${dir}/index.tsx`];
  if (!load) throw new Error(`${dir}: has meta.ts but no index.tsx`);
  if (mod.meta.id !== folder) throw new Error(`${dir}: meta.id "${mod.meta.id}" must equal the folder name`);
  registry[mod.meta.id] = { meta: mod.meta, load, Component: lazy(load) };
}

export const extensions: RegistryEntry[] = Object.values(registry).sort((a, b) => a.meta.id.localeCompare(b.meta.id));

/** URL a stack should point at for this extension, given where the app is hosted. */
export function extensionUrl(hostUrl: string, id: string): string {
  return `${hostUrl.replace(/\/$/, '')}/#/${id}`;
}
